import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * AdminTile — shared mobile-first navigation tile for Admin hub +
 * section pages (Feb 6, 2026 admin restructure).
 *
 * Owner directive: "Admin should feel like a command centre with
 * clear doors into each area, not a messy top-tab strip."
 *
 * Usage:
 *   <AdminTile to="/admin/users" icon={Users} label="Users"
 *              desc="Learners, instructors, PDIs, roles" />
 *
 * Big tap target, single role: navigate to `to`. No other interactions.
 * `accent` is a tailwind color class for the icon bubble (blue / emerald
 * / violet / amber / rose / slate).
 */
export default function AdminTile({ to, icon: Icon, label, desc, testid, accent = "blue", badge = null }) {
  const accentMap = {
    blue:    "bg-blue-100 text-blue-600",
    emerald: "bg-emerald-100 text-emerald-600",
    violet:  "bg-violet-100 text-violet-600",
    amber:   "bg-amber-100 text-amber-600",
    rose:    "bg-rose-100 text-rose-600",
    slate:   "bg-slate-100 text-slate-600",
    indigo:  "bg-indigo-100 text-indigo-600",
  };
  const bubble = accentMap[accent] || accentMap.blue;

  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm hover:border-blue-300 hover:shadow-md active:scale-[0.99] transition-all"
    >
      <div className={`w-12 h-12 rounded-xl ${bubble} flex items-center justify-center flex-shrink-0`}>
        {Icon ? <Icon className="w-6 h-6" /> : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-base font-extrabold text-slate-900 leading-tight">{label}</p>
          {badge != null && Number(badge) > 0 && (
            <span
              data-testid={testid ? `${testid}-badge` : undefined}
              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10.5px] font-extrabold leading-none"
            >
              {Number(badge) > 99 ? "99+" : badge}
            </span>
          )}
        </div>
        {desc ? <p className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</p> : null}
      </div>
      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 flex-shrink-0" />
    </Link>
  );
}
